import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import { describeMembershipSessionUsage, hasMembershipSessionUsage, listCheckFlags, mismatchCount, screenCheckNeededCount, unpaidReviewCount, ReviewLevel } from '../../features/sessionReport/labels';
import { SessionReportMembership, SessionReportRow } from '../../features/sessionReport/types';

/**
 * 고른 달의 부르는 말. 진행 중인 달이면 「이번 달」「지금」, 지난 달이면 「9월」「9월 말」.
 * 칸 이름·뜻의 「이번 달」「지금」을 이 말로 바꿔 지난 달 숫자를 오늘 값으로 읽지 않게 한다.
 */
export const ReportMonthContext = createContext<{ month: string; at: string }>({ month: '이번 달', at: '지금' });
export const ReportExplanationContext = createContext({ expanded: false, onToggle: () => {} });

/** 화면의 음수 부호만 통일한다. 서버 숫자·날짜·접근성 계산식은 바꾸지 않는다. */
export const normalizeReportZero = (value: number) => Object.is(value, -0) || value === 0 ? 0 : value;
export const formatReportNumber = (value: number | string) =>
    (typeof value === 'number' ? normalizeReportZero(value).toLocaleString('ko-KR') : /^[-−]0$/.test(value) ? '0' : value).replace(/^-/, '−');

/** 회원 행 밖의 사유와 구서버의 미수 확인도 남은 할 일로 보여요. */
export const hasReportReviewWork = (reference: Parameters<typeof screenCheckNeededCount>[0]) =>
    screenCheckNeededCount(reference) > 0 || mismatchCount(reference) > 0
    || unpaidReviewCount(reference) > 0 || listCheckFlags(reference).length > 0;

/** 표의 빈 회차는 흐리게 표시한다. 엑셀의 숫자와 서식에는 적용하지 않는다. */
export const ReportTableNumber = ({ value, signed = false }: { value: number; signed?: boolean }) =>
    value === 0 ? <span className="text-[#4E5968]">-</span> : <>{signed && value > 0 ? '+' : ''}{formatReportNumber(value)}</>;

/** 합산하지 않고 같은 수강권의 서버 사용 회차와 총회차를 나란히 보여 준다. */
export const MembershipSessionUsage = ({ membership: m, isExpired }: { membership: SessionReportMembership; isExpired: boolean }) => {
    if (!hasMembershipSessionUsage(m)) return null;
    return <div className="min-w-0" role="group" aria-label="수강권 사용 회차">
        <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[2px] tabular-nums" style={{ color: m.remaining < 0 ? '#B91C1C' : '#202630' }}>
            <span className="text-[13px] font-medium text-[#4E5968]">쓴 회차</span>
            <strong className="text-[32px] font-semibold leading-[42px] tracking-[-.03em]">{formatReportNumber(m.usedSessions)}</strong>
            <span className="text-[26px] font-normal text-[#4E5968]">/</span>
            <span className="text-[13px] font-medium text-[#4E5968]">총회차</span>
            <strong className="text-[32px] font-semibold leading-[42px] tracking-[-.03em]">{formatReportNumber(m.totalSession)}</strong>
        </div>
        <p className="mt-[4px] break-keep text-[14px] leading-[22px] text-[#4E5968]">{describeMembershipSessionUsage(m, isExpired)}</p>
    </div>;
};

/** 만료 표도 수강권별 서버 숫자를 유지한다. 여러 장일 때 회원 합계를 새로 만들지 않는다. */
export const MemberRemainingSessions = ({ row, isExpired }: { row: SessionReportRow; isExpired: boolean }) => {
    const memberships = isExpired ? row.memberships.filter(hasMembershipSessionUsage) : [];
    if (!memberships.length) return <ReportTableNumber value={row.remaining} />;
    return <span className="inline-flex flex-wrap items-baseline justify-center gap-x-[6px] gap-y-[4px]">
        <ReportTableNumber value={row.remaining} />
        {memberships.map((m) => <span key={m.membershipId} className={`${row.memberships.length === 1 ? 'contents' : 'inline-flex flex-wrap items-center justify-center gap-[4px]'} text-[13px] font-normal leading-[19px] text-[#4E5968]`}>
            {row.memberships.length > 1 && <span className="w-full whitespace-normal break-words">{m.name}</span>}
            <span aria-label={`${m.name} · 쓴 회차 ${m.usedSessions} / 총회차 ${m.totalSession}`} className="tabular-nums">{formatReportNumber(m.usedSessions)}/{formatReportNumber(m.totalSession)}</span>
            {m.remaining > 0 && <span className="whitespace-nowrap rounded-[5px] bg-[#F2F4F6] px-[6px] py-[2px]">
                {`${formatReportNumber(m.remaining)}회 남기고 ${m.expiryPolicy === 'COUNT' ? '기간 지남' : '만료'}`}
            </span>}
        </span>)}
    </span>;
};

/** 표시 설정만 브라우저에 기억해요. 저장이 막혀도 화면 안에서는 여닫을 수 있어요. */
export const useReportDisclosure = (name: string, defaultExpanded = false) => {
    const key = `rappo:sessionReport:display:${name}`;
    const [expanded, setExpanded] = useState(() => {
        try {
            const saved = window.localStorage.getItem(key);
            return saved === null ? defaultExpanded : saved === '1';
        } catch {
            return defaultExpanded;
        }
    });
    useEffect(() => {
        try {
            window.localStorage.setItem(key, expanded ? '1' : '0');
        } catch {
            // 표시 설정을 저장하지 못해도 현재 화면은 그대로 쓸 수 있어요.
        }
    }, [key, expanded]);
    return [expanded, setExpanded] as const;
};

export const ExplanationToggle = () => {
    const { expanded, onToggle } = useContext(ReportExplanationContext);
    return (
        <button type="button" aria-expanded={expanded} onClick={onToggle} title="회차 카드와 모든 표의 칸 설명을 함께 여닫아요"
            className="shrink-0 min-h-[40px] px-[8px] text-[14px] leading-[20px] text-[#4E5968] hover:text-[#333D4B]">
            {expanded ? '칸 설명 접기' : '칸 설명 보기'}
        </button>
    );
};
const useMonthWords = () => {
    const { month, at } = useContext(ReportMonthContext);
    return (text: string) => text.replace(/이번 달/g, month).replace(/^지금 /, `${at} `);
};

export const REVIEW_TONES: Record<ReviewLevel, { label: string; bg: string; text: string; border: string }> = {
    fix: { label: '직접 고칠 것', bg: '#FEF2F2', text: '#B91C1C', border: '#FECACA' },
    ask: { label: '라포가 확인할 것', bg: '#EFF5FF', text: '#3D73DD', border: '#D9E6FF' },
    review: { label: '확인만 하면 되는 것', bg: '#F4F5F7', text: '#4E5968', border: '#E5E8EB' },
    info: { label: '할 일 없음', bg: '#F4F5F7', text: '#4E5968', border: '#E4E4E4' },
};

// 엑셀의 기존 이름은 바꾸지 않아요. 근무시간 구분은 화면에서도 IN·OUT으로 표시해요.
const WORDS: Record<string, { name: string; old?: string; meaning: string; hideOld?: boolean; noGloss?: boolean }> = {
    강사: { name: '강사', meaning: '이름을 누르면 그 강사의 회원을 볼 수 있어요.', noGloss: true },
    // 서버의 신규·변경·리뉴 금액은 그 달에 들어온 결제(나눠 낸 돈 포함)라 「판매」라고 부르지 않는다.
    신규: { name: '신규', meaning: '처음 산 수강권으로 이번 달에 들어온 결제 금액과 늘어난 회차예요.' },
    '이번 달 결제': { name: '이번 달 결제', old: '신규·변경·리뉴', meaning: '이번 달에 들어온 결제 금액과 늘어난 회차예요. 예전에 산 수강권의 나눠 낸 돈도 들어가요. 자세히 보기에서 신규·회차 추가·재등록으로 나눠 볼 수 있어요.' },
    변경: { name: '회차 추가', meaning: '쓰던 수강권에 회차를 더하면서 이번 달에 들어온 결제 금액과 회차예요.' },
    리뉴: { name: '재등록', meaning: '다시 산 수강권으로 이번 달에 들어온 결제 금액과 늘어난 회차예요.' },
    전월잔여: { name: '지난달 남은', meaning: '지난달 마지막 날에 회원들에게 남아 있던 회차예요.' },
    당월등록: { name: '새로 판', meaning: '이번 달에 새로 결제해서 늘어난 회차예요.' },
    당월소진: { name: '수업', meaning: '이번 달에 출석과 예약 후 불참으로 쓴 회차예요.' },
    당월완료: { name: '수업', meaning: '이번 달에 출석과 예약 후 불참으로 쓴 회차예요.' },
    진행세션: { name: '수업', meaning: '이번 달에 출석과 예약 후 불참으로 쓴 회차예요.' },
    IN: { name: 'IN 수업', meaning: '강사의 근무시간에 진행한 수업이에요. OUT 수업이 있는 센터에서만 따로 보여요.' },
    OUT: { name: 'OUT 수업', meaning: '강사의 근무시간을 벗어나 진행한 수업이에요. OUT 수업이 없는 센터에서는 숨겨요.' },
    인계: { name: '강사 간 넘김', old: '인계 내역', meaning: '담당 강사를 바꾸거나 양도해서 주고받은 회차예요. 준 쪽은 빼고 받은 쪽은 더해요.' },
    인계세션: { name: '강사 간 넘김', meaning: '담당 강사를 바꾸거나 양도해서 주고받은 회차예요. 준 쪽은 빼고 받은 쪽은 더해요.' },
    '취소·정리': { name: '취소·정리', hideOld: true, meaning: '발급 취소·삭제·인계로 정리된 회차예요.' },
    환불: { name: '환불', meaning: '환불로 줄어든 회차와 돌려준 금액이에요.' },
    환불세션: { name: '환불', meaning: '환불로 줄어든 회차예요.' },
    환불금액: { name: '돌려준 돈', meaning: '환불로 회원에게 돌려준 금액이에요.' },
    남은세션: { name: '남은 회차', meaning: '기록상 회원들에게 남아 있는 회차예요(만료돼 못 쓰는 회차도 들어 있어요). 진행 중인 달은 지금, 지난 달은 그 달 마지막 날 기준이고, 달 중간에 센 값이면 센 시각 기준이에요.' },
    계약미수금: { name: '미수금', meaning: '판매 금액 중 아직 받지 못한 돈이에요. 지난 달을 봐도 오늘 기준이에요.' },
    '미수(현재)': { name: '미수금', meaning: '아직 받지 못한 돈이에요. 지난달을 봐도 오늘 기준이며, 셀 수 없으면 「확인 필요」로 보여요.' },
    확인: { name: '확인할 일', meaning: '기록을 고치거나 한 번 봐야 하는 회원 기록 수예요. 회원별 표의 한 줄을 한 건으로 세어요. 색으로 급한 정도를 나누고, 누르면 이유와 할 일이 나와요.' },
    회원명: { name: '회원', meaning: '이름을 누르면 회원의 수강권 정보를 볼 수 있어요.', hideOld: true, noGloss: true },
    // 엑셀에는 이 칸이 없고 시트(유효기간내·유효기간외·유령)로 나뉜다 — 작은 글씨를 달지 않는다.
    유효기간: { name: '기간', hideOld: true, meaning: '기간 지남은 만료일이 지났는데 회차가 남은 수강권이에요. 기간 안은 그 밖의 수강권(만료일이 안 지났거나, 지났어도 남은 회차가 없는 것)이에요.' },
    구분: { name: '수강권', meaning: '수강권 이름과 신규·재등록·공유·1:2·무제한 같은 종류를 보여요.' },
    등록금액: { name: '이번 달 결제 금액', meaning: '이번 달에 들어온 결제 금액이에요. 예전에 산 수강권의 나눠 낸 돈도 들어가요.' },
    세션: { name: '새로 판', meaning: '이번 달에 새로 결제해서 늘어난 회차예요.' },
    담당: { name: '주고받은 강사', meaning: '회차를 주고받은 강사와 회차예요. 1:2 대표가 옮겨간 내역도 따로 보여요.' },
};

/** 기존 칸 이름(대부분 엑셀 칸 이름). 화면 이름과 같거나 굳이 적을 필요가 없으면 없어요. */
const oldNameOf = (column: string) => {
    const word = WORDS[column];
    if (!word || word.hideOld) return '';
    const old = word.old ?? column;
    return old === word.name ? '' : old;
};

export const ReportColumnTitle = ({ column, align = 'center', wrap = false, showExcelName = false }: { column: string; align?: 'center' | 'start'; wrap?: boolean; showExcelName?: boolean }) => {
    const word = WORDS[column];
    const old = oldNameOf(column);
    const say = useMonthWords();
    return (
        <span className={`inline-flex flex-col ${align === 'start' ? 'items-start' : 'items-center'} py-[10px] text-[15px] leading-[24px] font-medium text-[#3D4244] ${wrap ? 'break-keep' : 'whitespace-nowrap'}`}>
            <span>{say(word?.name ?? column)}</span>
            {showExcelName && old && <span className="mt-[2px] text-[14px] leading-[18px] font-normal text-[#4E5968]">엑셀: {old}</span>}
        </span>
    );
};

/** 표 아래 칸 뜻. 설명이 필요 없는 칸(강사·회원)은 빼고, 새 이름이 같은 칸은 한 번만 적어요. */
export const ColumnGlossary = ({ columns }: { columns: string[] }) => {
    const { expanded } = useContext(ReportExplanationContext);
    const say = useMonthWords();
    const seen = new Set<string>();
    const items = columns.flatMap((column) => {
        const word = WORDS[column];
        if (!word || word.noGloss || seen.has(word.name)) return [];
        seen.add(word.name);
        return [{ column, word, old: oldNameOf(column) }];
    });
    if (!expanded || items.length === 0) return null;
    return (
        <div className="mt-[12px] mb-[24px] rounded-[12px] bg-[#F8F9FB] px-[20px] py-[16px]">
            <p className="text-[14px] leading-[20px] text-[#4E5968]">칸 이름의 뜻이에요. 작은 회색 글씨는 대부분 엑셀의 칸 이름이에요. 표의 순서는 엑셀과 다를 수 있어요.</p>
            <dl className="grid grid-cols-1 xl:grid-cols-2 gap-x-[32px] gap-y-[10px] mt-[12px]">
                {items.map(({ column, word, old }) => (
                    <div key={column} className="text-[14px] leading-[22px] break-keep">
                        <dt className="font-medium text-[#333D4B]">
                            {say(word.name)}
                            {old && <span className="ml-[6px] text-[14px] font-normal text-[#4E5968]">엑셀: {old}</span>}
                        </dt>
                        <dd className="text-[#4E5968]">{word.meaning.replace(/이번 달/g, say('이번 달'))}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};

export const DetailToggle = ({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) => (
    <div role="group" aria-label="표 보기 방식" className="inline-flex shrink-0 rounded-[10px] border border-solid border-[#E1E6ED] bg-[#EDF0F5] p-[3px]">
        {[false, true].map((all) => <button key={String(all)} type="button" aria-pressed={expanded === all}
            onClick={() => { if (expanded !== all) onToggle(); }}
            className={`min-h-[34px] rounded-[7px] px-[12px] text-[13px] font-medium ${expanded === all ? 'bg-white text-[#2B5DB3] shadow-sm' : 'text-[#4E5968]'}`}>
            {all ? '엑셀 칸 모두' : '기본 보기'}
        </button>)}
    </div>
);

/** 계산 기준·안내 문구의 옛 용어만 풀어 써요. 수강권 이름(센터가 지은 이름)에는 쓰지 않아요 — 「리뉴얼」이 「재등록얼」이 된다. */
export const screenReportText = (text: string) => text
    .replace(/결제 없이 고친 회차\(수강권 직접 수정·시작 회차 정정\)는 전월 잔여에 반영돼 따로 드러나지 않아요/g,
        '결제 없이 직접 고친 회차는 지난달 남은 회차에 들어 있어요. 별도 항목으로는 보이지 않아요.')
    .replace(/리뉴/g, '재등록')
    .replace(/없어진 회차|기타 조정/g, '취소·정리')
    .replace(/새로 산 회차|새로 판 회차/g, '새로 판')
    .replace(/지금 남은 회차|수강권에 남은/g, '남은 회차')
    .replace(/강사끼리 넘김/g, '강사 간 넘김')
    .replace(/「변경」/g, '「회차 추가」').replace(/전월 잔여/g, '지난달 남은 회차')
    .replace(/삭제된 결제는 빼요/g, '삭제된 결제는 계산에서 뺐어요.');

/** 건수 기준 같은 보충 안내는 마우스·키보드·터치로 열 수 있어요. */
export const ReportInfoTip = ({ label, children }: { label: string; children: React.ReactNode }) => {
    const id = useId();
    const [open, setOpen] = useState(false);
    return (
        <span className="relative inline-flex shrink-0" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <button type="button" aria-label={label} aria-describedby={open ? id : undefined}
                onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen(true)}
                onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
                className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-full text-[14px] text-[#4E5968] hover:bg-[#F2F4F6]">ⓘ</button>
            {open && <span id={id} role="tooltip" className="absolute right-0 top-full z-50 w-[280px] max-w-[70vw] rounded-[10px] bg-[#333D4B] p-[14px] text-left text-[14px] font-normal leading-[21px] text-white shadow-lg">{children}</span>}
        </span>
    );
};
